import { Component, inject, Input, OnInit } from '@angular/core'
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { BaseComponent } from '../../../common/components/base.component'
import { NotificationService } from '../../../common/services/notification.service'
import { ValidateCardNumberService } from '../../../common/services/validate-card-number.service'
import { CommonUtil } from '../../../common/util/common-util'
import { LanguageStateService } from '../../../common/services/language-state.service'
import { luhnValidator } from '../../../common/validators/luhn.validator'
import { toRequirementFlag } from '../../../common/utils/requirement-flag'
import { CARD_IMAGES, CARD_PATTERNS } from '../../../common/constants/credit-card'

export interface CardValidationResponse {
  IsValid: boolean
  CardType: CardTypeEnum
}

enum CardTypeEnum {
  Amex = 0,
  Visa = 1,
  Master = 2,
  Discover = 3,
  Invalid = 4
}

@Component({
    selector: 'app-credit-card-billing',
    templateUrl: './credit-card-billing.component.html',
    styleUrl: './credit-card-billing.component.scss',
    standalone: false
})
export class CreditCardBillingComponent extends BaseComponent implements OnInit {

  #fb: FormBuilder = inject(FormBuilder)
  #notificationService = inject(NotificationService)
  validateCardNumberService = inject(ValidateCardNumberService)
  languageService: LanguageStateService = inject(LanguageStateService)
  creditCardForm: FormGroup
  cardValidationStatus: boolean | null = null
  cardValidationImage: string = CARD_IMAGES.Accepted
  cardType: number

  public cardNumberRaw: string = ''
  public expirationDateRaw: string = ''
  public cvvRaw: string = ''
  public zipCodeRaw: string = ''

  isValidCard: boolean
  isCheckingCard = false
  #checkedCardNumber = ''

  @Input() PaymentZipMustMatchService = false
  @Input() ServiceAvailabilityZipCode = ''
  @Input() customerName = ''

  initFormGroups() {
    this.creditCardForm = this.#fb.group({
      cardholderName: [this.customerName, [Validators.required, Validators.pattern(CARD_PATTERNS.CardholderName)]],
      zipCode: ['', [Validators.required, Validators.pattern(CARD_PATTERNS.ZipCode), this.samezipCodeValidator()]],
      cardNumber: ['', [Validators.required, Validators.pattern(CARD_PATTERNS.CardNumber), luhnValidator()]],
      expirationDate: ['', [Validators.required, Validators.pattern(CARD_PATTERNS.ExpirationDate)
        , this.expirationDateValidator()]],
      cvv: ['', [Validators.required, Validators.pattern(CARD_PATTERNS.Cvv)]]
    })
  }

  constructor() {
    super()
  }

  ngOnInit(): void {
    this.initFormGroups()
  }


  samezipCodeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (toRequirementFlag(this.PaymentZipMustMatchService) && control.value?.length === 5 && control.value !== this.ServiceAvailabilityZipCode?.split('-')[0]) {
        return { sameZipError: true }
      }
      return null
    }
  }

  expirationDateValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) {
        return null
      }

      const [month, year] = control.value.split('/').map(Number)

      const currentDate = new Date()
      const fullYear = year

      const expirationDate = new Date(fullYear, month - 1, 1)
      expirationDate.setMonth(expirationDate.getMonth() + 1)
      expirationDate.setDate(expirationDate.getDate() - 1)

      return expirationDate < currentDate
        ? { 'expired': { value: control.value } }
        : null
    }
  }

  onValidateCardNumber() {
    const cardNumberControl = this.creditCardForm.get('cardNumber')
    if (cardNumberControl.hasError('luhn')) return;
    if (this.cardNumberRaw === this.#checkedCardNumber && (this.isCheckingCard || this.isValidCard !== undefined)) return;
    this.cardValidationImage = CARD_IMAGES.Accepted
    if (this.cardNumberRaw?.length >= 15) {
      const cardNumber = this.cardNumberRaw
      this.#checkedCardNumber = cardNumber
      this.cardValidationStatus = null
      this.isCheckingCard = true
      this.subscriptions.add(
        this.validateCardNumberService.validateCardNumber(Number(cardNumber)).subscribe({
          next: (response: CardValidationResponse) => {
            if (cardNumber !== this.cardNumberRaw) return
            this.isCheckingCard = false
            if (response) {
              this.cardValidationImage = this.getCardImage(response.CardType, response.IsValid)
              this.cardType = response.CardType
              this.isValidCard = response.IsValid

              // Add validation error when card is invalid
              if (!response.IsValid) {
                cardNumberControl.setErrors({ invalidCard: true })
                this.cardValidationStatus = false
              } else {
                // Create a new errors object without the invalidCard property
                const currentErrors = cardNumberControl.errors;

                if (currentErrors) {
                  const newErrors = Object.entries(currentErrors)
                    .filter(([key]) => key !== 'invalidCard')
                    .reduce((obj, [key, value]) => {
                      obj[key] = value
                      return obj
                    }, {})

                  cardNumberControl.setErrors(Object.keys(newErrors).length ? newErrors : null)
                }
                this.cardValidationStatus = true
              }
            }
          },
          error: () => {
            if (cardNumber !== this.cardNumberRaw) return
            this.isCheckingCard = false
            this.cardValidationStatus = false
            this.cardValidationImage = CARD_IMAGES.Invalid
            this.#notificationService.showError('Card number validation failed.')
            cardNumberControl.setErrors({ invalidCard: true })
          }
        })
      )
    }
  }

  // The card is only checked on blur, so an autofilled number (no blur) was never checked and blocked Submit silently.
  validateCardIfNotChecked(): void {
    if (this.isValidCard === undefined && !this.isCheckingCard) {
      this.onValidateCardNumber()
    }
  }

  getCardImage(cardType: CardTypeEnum, isValidCard: boolean): string {
    if (!isValidCard) return CARD_IMAGES.Invalid
    switch (cardType) {
      case CardTypeEnum.Amex:
        return CARD_IMAGES.Amex
      case CardTypeEnum.Visa:
        return CARD_IMAGES.Visa
      case CardTypeEnum.Master:
        return CARD_IMAGES.MasterCard
      case CardTypeEnum.Discover:
        return CARD_IMAGES.Discover
      default:
        return '' // Default fallback (could be an error image or blank)
    }
  }

  onCardNumberChange(value): void {
    this.cardNumberRaw = value?.replace(CARD_PATTERNS.CardNumberMaskChars, '') ?? ''
    if (this.cardNumberRaw === this.#checkedCardNumber) return
    this.clearCardCheck()
    if (this.isCompleteCardNumber(this.cardNumberRaw)) {
      this.onValidateCardNumber()
    }
  }

  clearCardCheck(): void {
    this.#checkedCardNumber = ''
    this.cardType = undefined
    this.isValidCard = undefined
    this.isCheckingCard = false
    this.cardValidationStatus = null
    this.cardValidationImage = CARD_IMAGES.Accepted
  }

  // Amex is 15 digits and the other accepted brands 16, so typed or autofilled numbers are checked without waiting for blur
  isCompleteCardNumber(cardNumber: string): boolean {
    return cardNumber.length === 16 || (cardNumber.length === 15 && CARD_PATTERNS.AmexPrefix.test(cardNumber))
  }

  onExpirationDateChange(value): void {
    this.expirationDateRaw = value
  }

  onCvvChange(value): void {
    this.cvvRaw = value
  }

  onZipCodeChange(value): void {
    const digitsOnly = (value ?? '').replace(CARD_PATTERNS.NonDigits, '').slice(0, 5)
    if (digitsOnly !== value) {
      this.creditCardForm.get('zipCode')?.setValue(digitsOnly, { emitEvent: false })
    }
    this.zipCodeRaw = digitsOnly
  }

  onlyAlphabets(event: KeyboardEvent): boolean {
    return CommonUtil.onlyAlphabets(event)
  }

  onlyNumbers(event: KeyboardEvent): boolean {
    return CommonUtil.onlyNumbers(event)
  }

  onlyNumbersAndDash(event: KeyboardEvent): boolean {
    return CommonUtil.onlyNumbersAndDash(event)
  }

  onlyNumbersAndSlash(event: KeyboardEvent): boolean {
    return CommonUtil.onlyNumbersAndSlash(event)
  }

}


// @NgModule({
//   declarations: [
//     CreditCardBillingComponent
//   ],
//   imports: [
//     AppBaseModule,
//     SaraPlusFormModule,
//     AppLayoutModule,
//     MaskInputDirective
//   ],
//   exports: [
//     CreditCardBillingComponent
//   ],
//   providers: [
//     { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
//   ]
// })
// export class CreditCardBillingModule {

// }
